import { type CollectionSummaryJSONObject, IkeToken } from '@ikegps/core-api';
import { CollectionModelFieldAnchor, ColorHash, FormModelFieldSubForm } from '@ikegps/core-shared';
import {
	type LineFieldPolylineClasses,
	lineFieldPolylineNodes,
	lineFieldPolylineStyles,
} from '../../components/line.field.polyline';
import { addStyles, DefaultTheme, rgba } from '../../components/theme';
import {
	ANCHOR_PINK,
	BLACK,
	ColorPalette2022Branding,
	ColorPaletteBlue,
	ColorPaletteOrange,
} from '../../components/theme.palette';
import { getShowRedDot } from '../../components/red.dot/is.missing.true.size';
import { DRAGON_ID, DRAGON_TYPE, DragonDomTypes } from '../../dom';
import { T, Translate } from '../../i18n/index';
import { m, type MithrilVNode } from '../../mithril';
import { Services } from '../../services';
import { CurrentState } from '../../state/page.state';
import { AppStore } from '../../store';
import { PointCache } from '../google/projection.cache';
import { PointCollectionCache } from './point.collection.cache';
import type { TagMapState } from './tag.map.state';

const CSS = {
	container: 'c-MapCollection',
	title: 'c-MapCollection__title',
	titleText: 'c-MapCollection__titleText',
	point: 'c-MapCollection__point',
	line: 'c-MapCollection__line',
	isHovered: 'c-MapCollection--is-hovered',
	isSelected: 'c-MapCollection--is-selected',
	isInspected: 'c-MapCollection--is-inspected',
	redDot: 'c-MapCollection__redDot',
};

const CIRCLE_RADIUS = DefaultTheme.spacingFactor * 1.5;
const CIRCLE_SIZE_PX = CIRCLE_RADIUS * 2 + 'px';
const CIRCLE_BORDER_SIZE_THICK = 3;
const CIRCLE_BORDER_SIZE = 2;
const TITLE_WIDTH = 120;
const TITLE_PADDING = DefaultTheme.spacingFactor;

const ZOOM_LINE_LEVEL = 18;
const SITE_TYPE_COLORS = {
	1: 'rgb(82,11,144)', // purple
	2: 'rgb(72,187,120)', // green
};
const DROP_POLE_COLORS = {
	1: 'rgb(255,0,0)', // red
	2: 'rgb(0,255,0)', // green
};

addStyles({
	[CSS.container]: {
		position: 'absolute',
	},
	[CSS.point]: {
		height: CIRCLE_SIZE_PX,
		width: CIRCLE_SIZE_PX,
		minWidth: CIRCLE_SIZE_PX,
		maxWidth: CIRCLE_SIZE_PX,
		borderRadius: '50%',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		position: 'absolute',
		border: `${CIRCLE_BORDER_SIZE}px solid ${rgba(BLACK, 0.5)}`,
	},
	[`${CSS.isHovered}, .${CSS.isSelected}, .${CSS.isInspected}`]: {
		zIndex: 99,
	},
	[`${CSS.point}.${CSS.isSelected}`]: {
		boxShadow: `0 0 0 3px rgba(${ColorPaletteOrange.C700}, 0.24), 0 0 0 3px rgba(${ColorPaletteOrange.C700}, 0.64)`,
		border: `${CIRCLE_BORDER_SIZE}px solid rgba(255, 255, 255, 0.87)`,
	},
	[`${CSS.point}.${CSS.isInspected}`]: {
		boxShadow: `0 0 0 3px rgba(${ColorPaletteBlue.C700}, 0.24), 0 0 0 3px rgba(${ColorPaletteBlue.C700}, 0.64)`,
		border: `${CIRCLE_BORDER_SIZE}px solid rgba(255, 255, 255, 0.87)`,
	},
	[`${CSS.point}.${CSS.isHovered}`]: {
		cursor: 'pointer',
		boxShadow: DefaultTheme.shadow.box.dp2w5,
	},
	[`${CSS.point}.${CSS.isHovered}.${CSS.isSelected}`]: {
		boxShadow: `0 0 0 5px rgba(${ColorPaletteOrange.C700}, 0.24), 0 0 0 5px rgba(${ColorPaletteOrange.C700}, 0.64)`,
	},
	[CSS.title]: {
		fontFamily: DefaultTheme.font.familyCondensed,
		fontWeight: DefaultTheme.font.weight.bold,
		height: DefaultTheme.space(6),
		lineHeight: DefaultTheme.space(6),
		fontSize: '125%',
		position: 'absolute',
		width: `${TITLE_WIDTH}px`,
		textAlign: 'center',
		'pointer-events': 'none',
	},
	[CSS.titleText]: {
		backgroundColor: '#FFF',
		boxShadow: DefaultTheme.shadow.box.dp2,
		padding: `0 ${TITLE_PADDING}px`,
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
	},
	[CSS.redDot]: {
		position: 'absolute',
		top: '-2px',
		right: '-2px',
		width: '6px',
		height: '6px',
		'border-radius': '50%',
		'background-color': ColorPalette2022Branding.lightRed,
		border: '1px solid #FFFFFF',
		'box-shadow': DefaultTheme.shadow.box.dp2,
		'pointer-events': 'none',
	},
});

const GREY_COLOR = 'rgb(160,174,192)';

// Saved line-field polylines on the tag/PM map. Distinct from the topo classes so each surface
// owns its own (print-capture matches the topo names by string).
const LINE_FIELD_CLASSES: LineFieldPolylineClasses = {
	container: 'c-MapOverlayPolyline',
	vector: 'c-MapOverlayPolyline__vector',
	highlight: 'c-MapOverlayPolyline__highlight',
	clickTarget: 'c-MapOverlayPolyline__clickTarget',
};

addStyles(lineFieldPolylineStyles(LINE_FIELD_CLASSES));

export const CollectionMapPoint = {
	/**
	 * A collection's map presence: the dot marking where it stands, plus its lines.
	 *
	 * `point` is null when the collection has no location. The dot is skipped in that case — a dot
	 * asserts a position, and this collection has none — while its drawn lines still render, because
	 * a line carries its own coordinates and never needed the collection to be placed.
	 */
	view(
		state: TagMapState,
		summary: CollectionSummaryJSONObject,
		point: google.maps.Point | null
	): { collection: MithrilVNode | null; line: MithrilVNode[] } {
		const hoverId = CurrentState.hoverId;
		const isHovered = hoverId === summary.revision;
		const isSelected = CurrentState.Tag.isCollectionSelected(summary.tags[0], summary.uuid);
		const isInspected = CurrentState.inspectedCollectionRevision === summary.revision;
		const isTagHovered = summary.tags.indexOf(hoverId) > -1;
		const isDetailShown = isHovered || state.showDetail;

		const stateCSS = `${isInspected ? CSS.isInspected : ''} ${isSelected ? CSS.isSelected : ''} ${isTagHovered || isHovered ? CSS.isHovered : ''}`;

		const collection =
			point != null
				? CollectionMapPoint.viewDot(state, summary, point, { stateCSS, isInspected, isDetailShown })
				: null;

		if (isSelected || isInspected || isDetailShown || state.zoom > ZOOM_LINE_LEVEL) {
			const lines: MithrilVNode[] = [];

			// Vectors and anchors are drawn relative to the collection's position, so they need one.
			if (
				point != null &&
				(Services.Token.has(IkeToken.FEATURE_MAP_VECTOR) || Services.Token.has(IkeToken.FEATURE_GROUP_IKE_ANALYZE))
			) {
				const vectors = CollectionMapPoint.viewLines(state, summary, point);
				if (vectors) {
					lines.push(...vectors);
				}
			}

			// Saved line fields render whenever the model is loaded — viewing isn't analyze-gated.
			const lineFields = CollectionMapPoint.viewLineFields(summary);
			if (lineFields) {
				lines.push(...lineFields);
			}

			if (lines.length) {
				return { collection, line: lines };
			}
		}

		return { collection, line: null };
	},

	/** The collection dot itself. Only ever built for a collection with a real location. */
	viewDot(
		state: TagMapState,
		summary: CollectionSummaryJSONObject,
		point: google.maps.Point,
		{ stateCSS, isInspected, isDetailShown }: { stateCSS: string; isInspected: boolean; isDetailShown: boolean }
	): MithrilVNode {
		const showRedDot = getShowRedDot(summary);

		let collection = m(
			'div',
			{
				key: summary.objectId,
				[DRAGON_ID]: summary.revision,
				[DRAGON_TYPE]: DragonDomTypes.COLLECTION,
				className: `${CSS.point} ${stateCSS}`,
				style: {
					// backgroundColor: ColorHash.hash(summary.tags[0]),
					top: point.y - CIRCLE_RADIUS - CIRCLE_BORDER_SIZE + 'px',
					left: point.x - CIRCLE_RADIUS - CIRCLE_BORDER_SIZE + 'px',
				},
				onclick: (e: MouseEvent) => {
					// Don't select/inspect a collection from a marker click while drawing a line.
					if (CurrentState.LineDraw.isActive) {
						return;
					}
					void state.handleCollectionClick(e, summary);
				},
			},
			showRedDot ? m('span', { className: CSS.redDot }) : undefined
		);

		// special collection coloring feature for Crown Castle
		// https://surveylab.atlassian.net/browse/IKE4-2997
		if (Services.Token.has(IkeToken.FEATURE_COLLECTION_COLORING)) {
			if (summary.siteType) {
				const siteTypeInt = parseInt(summary.siteType, 10);
				collection.attrs.style.backgroundColor = isNaN(siteTypeInt)
					? GREY_COLOR
					: SITE_TYPE_COLORS[siteTypeInt as keyof typeof SITE_TYPE_COLORS];
			} else if (!(summary.dropPole === null || summary.dropPole === undefined)) {
				collection.attrs.style.backgroundColor = summary.dropPole ? DROP_POLE_COLORS[1] : DROP_POLE_COLORS[2];
			} else {
				collection.attrs.style.backgroundColor = GREY_COLOR;
			}

			const borderColor = ColorHash.hash(summary.tags[0]);
			if (!isInspected) {
				collection.attrs.style.border = `${CIRCLE_BORDER_SIZE}px solid ${borderColor}`;
			}
		} else if (Services.Token.has(IkeToken.FEATURE_COLOR_FLAG)) {
			if (summary.mapMarkerColor) {
				collection.attrs.style.backgroundColor = summary.mapMarkerColor ?? GREY_COLOR;
				const borderColor = ColorHash.hash(summary.tags[0]);
				if (!isInspected) {
					collection.attrs.style.border = `${CIRCLE_BORDER_SIZE_THICK}px solid ${borderColor}`;
				}
			} else {
				//if mapMarkerColor is not set, use the job color
				collection.attrs.style.backgroundColor = ColorHash.hash(summary.tags[0]);
			}
		} else {
			collection.attrs.style.backgroundColor = ColorHash.hash(summary.tags[0]);
		}

		if (isDetailShown) {
			let title = summary.title;
			if (title == null) {
				title = Translate(T.COLLECTION_MISSING_TITLE);
			}

			delete collection.attrs.key;

			collection = m('div', { key: summary.objectId }, [
				collection,
				m(
					'div',
					{
						key: 'hovered-' + summary.objectId,
						[DRAGON_ID]: summary.revision,
						[DRAGON_TYPE]: DragonDomTypes.COLLECTION,
						className: `${CSS.title} ${stateCSS}`,
						style: {
							top: point.y - DefaultTheme.spacingFactor * 10 + 'px',
							left: point.x - TITLE_WIDTH / 2 + TITLE_PADDING / 2 + 'px',
						},
					},
					m('span', { className: CSS.titleText }, title)
				),
			]);
		}

		return collection;
	},

	/** Line-field polylines; non-interactive so they never steal map clicks. */
	viewLineFields(summary: CollectionSummaryJSONObject): MithrilVNode[] | null {
		const lineFields = PointCollectionCache.getCollectionLineFields(summary.revision);
		if (lineFields == null) {
			return null;
		}

		const nodes: MithrilVNode[] = [];
		lineFields.forEach((field) => {
			const node = lineFieldPolylineNodes(field, LINE_FIELD_CLASSES, (lat, lng) => PointCache.getPoint(lat, lng));
			if (node) {
				nodes.push(node);
			}
		});

		return nodes.length ? nodes : null;
	},

	viewLines(state: TagMapState, summary: CollectionSummaryJSONObject, point: google.maps.Point): MithrilVNode[] {
		const collection = AppStore.Collection.get(summary.revision);
		if (collection == null) {
			return null;
		}

		const location = PointCollectionCache.getCollectionLocation(summary.revision);
		if (location == null) {
			return null;
		}

		const lineInfos = PointCollectionCache.getCollectionVectors(summary.revision);

		const lines = [];

		const useColorCoding = Services.Token.has(IkeToken.PHOTOMEASURE_COLOR_CODING);

		for (const lineInfo of lineInfos) {
			const { field, target } = lineInfo;
			const targetPoint = PointCache.getPoint(target.latitude, target.longitude);
			const spanColor = FormModelFieldSubForm.getSpanColor(field.parent);
			let color = useColorCoding && spanColor ? spanColor : ColorHash.hash(field.schema.id);
			if (CollectionModelFieldAnchor.isAnchorField(field)) {
				color = `rgb(${ANCHOR_PINK})`;
			}
			const line = createLine(point.x, point.y, targetPoint.x, targetPoint.y, color);
			line.attrs[DRAGON_ID] = field.id;
			line.attrs[DRAGON_TYPE] = 'field';

			lines.push(line);
		}

		return lines;
	},
};

const LINE_WIDTH = 1;
const LINE_HIT_BOX = LINE_WIDTH * 8;

const LineCss = {
	container: 'c-MapLine',
	hitBox: 'c-MapLine__hitbox',
	lineFill: 'c-MapLine__fill',
	info: 'c-MapLine__info',
};

function createLine(x1: number, y1: number, x2: number, y2: number, fillColor: string, text?: string) {
	const topLeft = { x: Math.min(x1, x2), y: Math.min(y1, y2) };
	const bottomRight = { x: Math.max(x1, x2), y: Math.max(y1, y2) };

	const width = Math.abs(topLeft.x - bottomRight.x);
	const height = Math.abs(topLeft.y - bottomRight.y);

	const length = Math.sqrt(width * width + height * height);

	const angle = Math.atan2(y2 - y1, x2 - x1);

	const topHitBox = y1 - LINE_HIT_BOX / 2 + 'px';
	const left = x1 + 'px';

	const lineHitBox = m('div', {
		className: LineCss.hitBox,
		style: {
			width: `${length}px`,
			position: 'absolute',
			display: 'flex',
			top: topHitBox,
			left,
			height: LINE_HIT_BOX + 'px',
			transform: `rotate(${angle}rad)`,
			'transform-origin': `0px ${LINE_HIT_BOX / 2}px`,
		},
	});

	const line = m('div', {
		className: LineCss.lineFill,
		style: {
			width: `${length}px`,
			position: 'absolute',
			top: y1 + 'px',
			left,
			outline: '1px solid ' + fillColor,
			height: 0,
			transform: `rotate(${angle}rad)`,
			'transform-origin': '0px 0px',
		},
	});

	const children = [lineHitBox, line];

	return m('div', { className: LineCss.container }, children);
}
